import * as React from 'react';
import { Box, Container, Tab, Tabs, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectAssumedRole } from '../../app/slices/userSlice';
import ReportContainer from './ReportContainer';
import {
  selectPendingEvents,
  pendingEvents,
  selectPendingEventsStatus,
  loadPictureDayConnectionDetails,
  selectPictureDayConnectionDetails,
  selectPictureDayConnectionDetailsStatus,
  selectNotUploadedEvents,
  selectNotUploadedEventsStatus,
  loadNotUploadedEvents,
  selectQuarantineEvents,
  selectQuarantineEventsStatus,
  loadQuarantineEvents, selectTodayEventComplete, selectTodayEventCompleteStatus, loadTodayEmCompleteDetails
} from '../../app/slices/reportSlice';
import { ReportTableType } from '../../model/reportTableType';
import TabPanel from '../commons/TabPanel';
import styles from './FormsAndReport.module.css';
import { COMPLETE_ACCESS } from '../commons/AccesRoles';
import QuarantineDataFix from './QuarantineDataFix';
import QuarantineResolve from './QuarantineResolve';
import {
  loadProcessingLab,
  selectTerritories,
  selectTerritoriesFetchStatus
} from '../../app/slices/processingLabSlice';
import { useEffect } from 'react';

function FormsAndReports() {
  const dispatch = useAppDispatch();

  const userRole = useAppSelector(selectAssumedRole);
  const canAccessReports = COMPLETE_ACCESS.includes(userRole || '');

  const incompleteEvents = useAppSelector(selectPendingEvents);
  const eventReportStatus = useAppSelector(selectPendingEventsStatus);

  const todayUploadEvents = useAppSelector(selectPictureDayConnectionDetails);
  const todayUploadEventsStatus = useAppSelector(selectPictureDayConnectionDetailsStatus);

  const notUploadedEvents = useAppSelector(selectNotUploadedEvents);
  const notUploadedEventsStatus = useAppSelector(selectNotUploadedEventsStatus);

  const quarantineEvents = useAppSelector(selectQuarantineEvents);
  const quarantineEventsStatus = useAppSelector(selectQuarantineEventsStatus);

  const todayEmComplete = useAppSelector(selectTodayEventComplete);
  const todayEmCompleteStatus = useAppSelector(selectTodayEventCompleteStatus);

  const areas = useAppSelector(selectTerritories);
  const territoriesFetchStatus = useAppSelector(selectTerritoriesFetchStatus);

  useEffect(() => {
    if (!areas.length && !territoriesFetchStatus) {
      dispatch(loadProcessingLab());
    }
  }, []);

  const getIncompleteEventsByArea = (lastSixMonths: boolean) => {
    dispatch(pendingEvents({ areas: optionsSelected, lastSixMonths: lastSixMonths }));
  };

  const getTodayUploadEvents = () => {
    dispatch(loadPictureDayConnectionDetails(optionsSelected[0]));
  };

  const getNotUploadedEvents = (lastSixMonths: boolean) => {
    dispatch(loadNotUploadedEvents({ areas: optionsSelected, lastSixMonths: lastSixMonths }));
  };

  const getQuarantineEvents = (lastSixMonths: boolean, showResolved: boolean) => {
    setEventId('');
    dispatch(loadQuarantineEvents({ areas: optionsSelected, showResolved: showResolved }));
  };

  const getTodayEmCompleteDetails = () => {
    dispatch(loadTodayEmCompleteDetails(optionsSelected[0]));
  };

  const [optionsSelected, setOptionsSelected] = React.useState<string[]>([]);

  const setOptions = (options: string[]) => {
    setOptionsSelected(options);
  };

  const [tab, setTab] = React.useState(ReportTableType.EVENT.toString());

  const handleChange = (event: React.SyntheticEvent, newTab: string) => {
    setTab(newTab);
  };

  const [eventId, setEventId] = React.useState('');

  const selectedEventId = (eventId: string) => {
    setEventId(eventId);
  };

  return (
    <>
      <Box sx={{
        height: { xs: '80px', sm: '100px' },
        backgroundColor: '#f0f7fb',
        display: 'flex',
        alignItems: 'center',
        mb: 5,
      }}>
        <Container maxWidth="xl" sx={{ display: 'flex', fontSize: { xs: '1.4em', sm: '1.5em' }, fontWeight: 200 }}>
          Reports Dashboard
        </Container>
      </Box>
      {canAccessReports && (
        <>
          <Container maxWidth="xl" sx={{ textAlign: 'left' }}>
            <Tabs
              value={tab}
              onChange={handleChange}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
              variant="scrollable"
              scrollButtons={'auto'}
              allowScrollButtonsMobile
            >
              <Tab className={styles.tabHeading} label={'Open Events'} value={ReportTableType.EVENT.toString()} />
              <Tab
                className={styles.tabHeading}
                label={'Open Events Not Uploaded'}
                value={ReportTableType.NOT_UPLOADED_EVENT.toString()}
              />
              <Tab
                className={styles.tabHeading}
                label={'Picture Day Connection Status'}
                value={ReportTableType.PICTURE_DAY.toString()}
              />
              <Tab
                className={styles.tabHeading}
                label={'EM Completion Status'}
                value={ReportTableType.EM_COMPLETION_STATUS.toString()}
              />
              <Tab
                className={styles.tabHeading}
                label={'Quarantine Report'}
                value={ReportTableType.QUARANTINE_EVENT.toString()}
              />
            </Tabs>
            <TabPanel index={tab} value={ReportTableType.EVENT.toString()}>
              <ReportContainer
                loaded={eventReportStatus}
                getReportDataByFilter={getIncompleteEventsByArea}
                reportData={incompleteEvents}
                tableName={ReportTableType.EVENT}
                reportLabel="Open Events Past Last Picture Date"
                setOptions={setOptions}
                areaCode={areas}
              />
            </TabPanel>

            <TabPanel index={tab} value={ReportTableType.NOT_UPLOADED_EVENT.toString()}>
              <ReportContainer
                loaded={notUploadedEventsStatus}
                getReportDataByFilter={getNotUploadedEvents}
                reportData={notUploadedEvents}
                tableName={ReportTableType.NOT_UPLOADED_EVENT}
                setOptions={setOptions}
                reportLabel="Events that have not started uploading and are past the last picture date"
                areaCode={areas}
              />
            </TabPanel>

            <TabPanel index={tab} value={ReportTableType.PICTURE_DAY.toString()}>
              <ReportContainer
                loaded={todayUploadEventsStatus}
                getReportDataByFilter={getTodayUploadEvents}
                reportData={todayUploadEvents}
                tableName={ReportTableType.PICTURE_DAY}
                setOptions={setOptions}
                reportLabel="Picture Day Connection Status"
                areaCode={areas}
              />
            </TabPanel>

            <TabPanel index={tab} value={ReportTableType.EM_COMPLETION_STATUS.toString()}>
              <ReportContainer
                loaded={todayEmCompleteStatus}
                getReportDataByFilter={getTodayEmCompleteDetails}
                reportData={todayEmComplete}
                setOptions={setOptions}
                selectedEventId={selectedEventId}
                tableName={ReportTableType.EM_COMPLETION_STATUS}
                reportLabel="EM Completion Status"
                areaCode={areas}
              />
            </TabPanel>

            <TabPanel index={tab} value={ReportTableType.QUARANTINE_EVENT.toString()}>
              <ReportContainer
                loaded={quarantineEventsStatus}
                getReportDataByFilter={getQuarantineEvents}
                reportData={quarantineEvents}
                setOptions={setOptions}
                selectedEventId={selectedEventId}
                tableName={ReportTableType.QUARANTINE_EVENT}
                quarantineFix={userRole === 'AppSupportAdmin'}
                reportLabel="Quarantine Report"
                areaCode={areas}
              />
              {(userRole === 'AppSupportAdmin') && (quarantineEvents.length !== 0) &&
                <><QuarantineDataFix runReport={getQuarantineEvents} eventId={eventId}></QuarantineDataFix>
                  <QuarantineResolve eventId={eventId} runReport={getQuarantineEvents}></QuarantineResolve></>
              }
            </TabPanel>
          </Container>
        </>
      )}
      {!canAccessReports && (
        <Container maxWidth="xl" sx={{ textAlign: 'left' }}>
          <Typography variant="h2" sx={{ mt: '6px', ml: '9px', fontWeight: 'normal' }} color="error">
            You are not authorized
          </Typography>
        </Container>
      )}
    </>
  );
}

export default FormsAndReports;
